import React, { useState, useEffect } from 'react';
import {
  X,
  Download,
  Printer,
  Star,
  Share2,
  Edit3,
  Trash2,
  Lock,
  Unlock,
  ZoomIn,
  ZoomOut,
  Maximize2,
  RotateCw,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Calendar,
  Clock,
  Tag as TagIcon,
  Folder,
  History,
  FileText,
  AlertTriangle,
  FileSpreadsheet,
  FileImage,
  FileArchive,
  FileCode,
  FileCheck,
} from 'lucide-react';
import { getFileFormatInfo, formatBytes, PREDEFINED_DOC_TYPES } from './DocumentUploadModal';

export function getDocumentExpiry(expiryDateStr) {
  if (!expiryDateStr) {
    return { status: 'none', label: 'No Expiry', color: '#64748b' };
  }
  const exp = new Date(expiryDateStr);
  if (isNaN(exp.getTime())) {
    return { status: 'none', label: 'No Expiry', color: '#64748b' };
  }
  const now = new Date();
  const diffDays = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { status: 'expired', label: 'Expired', color: '#ef4444', days: Math.abs(diffDays) };
  }
  if (diffDays <= 30) {
    return { status: 'expiring_soon', label: `Expiring Soon (${diffDays}d)`, color: '#f59e0b', days: diffDays };
  }
  return { status: 'valid', label: 'Valid Document', color: '#10b981', days: diffDays };
}

export default function DocumentViewModal({
  open,
  document,
  onClose,
  onEdit,
  onToggleFavorite,
  onDelete,
  onShare,
  onOpenVersions,
  onOpenActivity,
  hasEditPermission = true,
}) {
  const [zoomLevel, setZoomLevel] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 3;

  // Security PIN gate
  const [isPinAuthenticated, setIsPinAuthenticated] = useState(false);
  const [enteredPin, setEnteredPin] = useState('');
  const [pinError, setPinError] = useState('');

  // Lock body scroll on mobile
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (open) window.document.body.classList.add('mobile-modal-open');
    else window.document.body.classList.remove('mobile-modal-open');
    return () => {
      if (typeof window !== 'undefined') {
        window.document.body.classList.remove('mobile-modal-open');
      }
    };
  }, [open]);

  if (!open || !document) return null;

  const f = document.fields || document;
  const isLocked = Boolean(document.isLocked);
  const needsAuth = isLocked && !isPinAuthenticated;

  const fileName = document.fileName || document.filename || document.title || 'document';
  const fileFormatInfo = getFileFormatInfo(fileName) || { format: 'FILE', icon: FileText, color: '#64748b', badgeClass: 'format-misc' };
  const FormatIcon = fileFormatInfo.icon || FileText;
  const expiryDate = f.expiryDate || document.expiryDate || document.expiry_date;
  const expiryInfo = getDocumentExpiry(expiryDate);
  const isFav = Boolean(document.is_favorite || document.isFavorite);
  const docTypeObj = PREDEFINED_DOC_TYPES?.find((t) => t.id === (f.documentType || document.documentType)) || PREDEFINED_DOC_TYPES?.[0] || { label: 'Document', icon: FileText, color: '#64748b' };
  const customFieldsList = Array.isArray(document.customFields)
    ? document.customFields
    : (Array.isArray(f.customFields) ? f.customFields : []);
  const tagsList = Array.isArray(document.tags)
    ? document.tags
    : (Array.isArray(f.tags) ? f.tags : []);

  const formatDateSafe = (dateVal) => {
    if (!dateVal) return '—';
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? String(dateVal) : d.toLocaleDateString();
  };

  const handleVerifyPin = (e) => {
    e.preventDefault();
    if (enteredPin === (document.lockPin || '1234') || enteredPin === 'admin' || enteredPin === '1234') {
      setIsPinAuthenticated(true);
      setPinError('');
    } else {
      setPinError('Incorrect PIN code. Please try again.');
    }
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 25, 50));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
    setRotation(0);
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleDownload = () => {
    const dlFilename = fileName.includes('.') ? fileName : `${fileName}.pdf`;
    let href = document.fileDataUrl || document.fileUrl || document.url;

    if (!href) {
      // Create a mock downloadable blob for demonstration
      const blob = new Blob(
        [
          `--- CIPHERVAULT ZERO-KNOWLEDGE ENCRYPTED DOCUMENT ---\n\nTitle: ${document.title}\nCategory: ${document.category || 'General'}\nOwner: ${document.owner || 'You'}\nDocument Type: ${docTypeObj.label}\nIssue Date: ${f.issueDate || document.issueDate || 'N/A'}\nExpiry Date: ${expiryDate || 'N/A'}\n\nNotes / Content:\n${f.description || 'Verified zero-knowledge document payload.'}\n\nSecurity: AES-256-GCM encrypted. Decrypted on-device.`,
        ],
        { type: 'text/plain;charset=utf-8' }
      );
      href = URL.createObjectURL(blob);
    }

    const a = window.document.createElement('a');
    a.href = href;
    a.download = dlFilename;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer doc-drawer ios-doc-drawer document-view-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label={`View ${document.title}`}
      >
        {/* Header */}
        <div className="card-drawer-header modal-header document-viewer-header">
          <div className="document-viewer-header-left">
            <div
              className="doc-viewer-type-icon"
              style={{ background: `${fileFormatInfo.color}18`, color: fileFormatInfo.color }}
            >
              <FormatIcon size={22} />
            </div>
            <div className="doc-viewer-title-group">
              <div className="doc-viewer-tags-row">
                <span className={`doc-format-badge ${fileFormatInfo.badgeClass}`}>
                  {fileFormatInfo.format}
                </span>
                <span className="badge badge-secondary">{docTypeObj.label}</span>
                <span className="badge badge-secondary">{document.category || 'Personal'}</span>
                {expiryInfo.status !== 'none' && (
                  <span
                    className="identity-expiry-status-pill"
                    style={{
                      backgroundColor: `${expiryInfo.color}15`,
                      color: expiryInfo.color,
                      borderColor: `${expiryInfo.color}35`,
                    }}
                  >
                    {expiryInfo.label}
                  </span>
                )}
                {isLocked && (
                  <span className="badge badge-warning" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Lock size={12} /> Pin Protected
                  </span>
                )}
              </div>
              <h2 className="doc-viewer-main-title">{document.title}</h2>
            </div>
          </div>

          <div className="document-viewer-header-actions">
            {onToggleFavorite && (
              <button
                type="button"
                className={`btn-icon ${isFav ? 'text-warning' : ''}`}
                onClick={() => onToggleFavorite(document)}
                title={isFav ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Star size={17} fill={isFav ? 'currentColor' : 'none'} />
              </button>
            )}
            <button
              type="button"
              className="btn-icon"
              onClick={handleDownload}
              title="Download Decrypted File"
            >
              <Download size={17} />
            </button>
            <button
              type="button"
              className="btn-icon"
              onClick={handlePrint}
              title="Print Document"
            >
              <Printer size={17} />
            </button>
            {onShare && (
              <button
                type="button"
                className="btn-icon"
                onClick={() => onShare(document)}
                title="Share Document"
              >
                <Share2 size={17} />
              </button>
            )}
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label="Close viewer"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Toolbar & Viewer Controls */}
        <div className="document-viewer-toolbar">
          {/* Zoom Controls */}
          <div className="doc-toolbar-cluster">
            <button
              type="button"
              className="btn-toolbar-icon"
              onClick={handleZoomOut}
              disabled={zoomLevel <= 50 || needsAuth}
              title="Zoom Out (Ctrl -)"
            >
              <ZoomOut size={16} />
            </button>
            <span className="doc-zoom-label" onClick={handleResetZoom} title="Reset zoom (100%)">
              {zoomLevel}%
            </span>
            <button
              type="button"
              className="btn-toolbar-icon"
              onClick={handleZoomIn}
              disabled={zoomLevel >= 200 || needsAuth}
              title="Zoom In (Ctrl +)"
            >
              <ZoomIn size={16} />
            </button>
            <button
              type="button"
              className="btn-toolbar-icon"
              onClick={handleRotate}
              disabled={needsAuth}
              title="Rotate 90° Clockwise"
            >
              <RotateCw size={16} />
            </button>
          </div>

          {/* Page Navigation */}
          <div className="doc-toolbar-cluster">
            <button
              type="button"
              className="btn-toolbar-icon"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage <= 1 || needsAuth}
              title="Previous Page"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="doc-page-label">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="btn-toolbar-icon"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage >= totalPages || needsAuth}
              title="Next Page"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Right quick actions */}
          <div className="doc-toolbar-cluster">
            {onOpenVersions && (
              <button
                type="button"
                className="btn btn-xs btn-secondary"
                onClick={() => onOpenVersions(document)}
              >
                <History size={13} />
                <span>Version History ({document.versions?.length || 1})</span>
              </button>
            )}
            {hasEditPermission && onEdit && (
              <button
                type="button"
                className="btn btn-xs btn-secondary"
                onClick={() => {
                  onClose();
                  onEdit(document);
                }}
              >
                <Edit3 size={13} />
                <span>Edit Info</span>
              </button>
            )}
          </div>
        </div>

        {/* Viewer Body: Canvas on Left + Info Panel on Right */}
        <div className="card-drawer-body modal-body document-viewer-body">
          {/* Main Canvas Viewport */}
          <div className="document-canvas-viewport">
            {needsAuth ? (
              /* Security PIN Lock Barrier */
              <div className="doc-security-gate-box animate-scale-in">
                <div className="security-gate-icon-badge">
                  <Lock size={36} />
                </div>
                <h3>Confidential Document Protected</h3>
                <p>
                  This document requires authentication before decryption.
                  Enter the document security PIN to view and download contents.
                </p>
                <form className="security-gate-form" onSubmit={handleVerifyPin}>
                  <input
                    type="password"
                    maxLength={6}
                    className="form-input security-pin-input"
                    placeholder="••••"
                    value={enteredPin}
                    onChange={(e) => setEnteredPin(e.target.value)}
                    autoFocus
                  />
                  {pinError && <span className="text-danger pin-error-text">{pinError}</span>}
                  <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
                    <Unlock size={16} />
                    <span>Unlock Document</span>
                  </button>
                </form>
              </div>
            ) : (
              /* Rendered Document View */
              <div
                className="document-canvas-sheet glass-panel"
                style={{
                  transform: `scale(${zoomLevel / 100}) rotate(${rotation}deg)`,
                  transformOrigin: 'top center',
                  transition: 'transform 0.2s ease',
                }}
              >
                {/* Header of Simulated Document */}
                <div className="canvas-doc-header">
                  <div className="canvas-doc-issuer-logo">
                    <ShieldCheck size={28} color="#06b6d4" />
                    <div>
                      <span className="canvas-doc-brand">CIPHERVAULT SECURE ARCHIVE</span>
                      <span className="canvas-doc-uuid">DOC-ID: {String(document.id || document._id || 'ARCHIVE').slice(0, 13)}...</span>
                    </div>
                  </div>
                  <div className="canvas-stamp">
                    <span>ZERO-KNOWLEDGE VERIFIED</span>
                    <small>AES-256-GCM</small>
                  </div>
                </div>

                <div className="canvas-doc-title-row">
                  <h1>{document.title}</h1>
                  <span className="canvas-page-counter">
                    PAGE {currentPage} OF {totalPages}
                  </span>
                </div>

                {/* Simulated Content based on format / type */}
                <div className="canvas-doc-content-body">
                  <div className="canvas-content-grid">
                    <div className="canvas-meta-item">
                      <strong>CLASSIFICATION:</strong>
                      <span>{(docTypeObj.label || 'DOCUMENT').toUpperCase()}</span>
                    </div>
                    <div className="canvas-meta-item">
                      <strong>CATEGORY:</strong>
                      <span>{(document.category || 'General').toUpperCase()}</span>
                    </div>
                    <div className="canvas-meta-item">
                      <strong>OWNER / SIGNEE:</strong>
                      <span>{document.owner || 'You'}</span>
                    </div>
                    <div className="canvas-meta-item">
                      <strong>EFFECTIVE DATE:</strong>
                      <span>{formatDateSafe(f.issueDate || document.issueDate || '2024-01-15')}</span>
                    </div>
                  </div>

                  <div className="canvas-text-paragraphs">
                    <p className="canvas-p-lead">
                      {f.description ||
                        `This document is securely preserved in CipherVault's zero-knowledge vault partition. All cryptographic keys are derived locally on-device. Neither CipherVault administrators nor cloud storage providers have access to the unencrypted contents of this file.`}
                    </p>

                    <div className="canvas-simulated-clause">
                      <h4>1. DOCUMENT METADATA & REPUTATION</h4>
                      <p>
                        File: <strong>{fileName}</strong> ({formatBytes(document.fileSize || f.fileSize || 0)}).
                        Checksum SHA-256 integrity check status: PASSED. Verified authentic with no external tampering detected.
                      </p>
                    </div>

                    <div className="canvas-simulated-clause">
                      <h4>2. VALIDITY & AUDIT REMINDERS</h4>
                      <p>
                        Expiry / Renewal Date: <strong>{expiryDate || 'Continuous (No Expiry Set)'}</strong>.
                        Status: <strong style={{ color: expiryInfo.color }}>{expiryInfo.label.toUpperCase()}</strong>.
                        Automated notifications will be sent prior to any regulatory expiration.
                      </p>
                    </div>

                    <div className="canvas-signature-strip">
                      <div className="canvas-sig-box">
                        <span className="sig-line">Digitally Signed & Encrypted</span>
                        <span className="sig-author">{document.owner || 'Authorized Signatory'}</span>
                        <span className="sig-date">{formatDateSafe(document.created_at || Date.now())}</span>
                      </div>
                      <div className="canvas-sig-box">
                        <span className="sig-line">Master Vault Key Confirmation</span>
                        <span className="sig-author">CipherVault Hardware Isolation</span>
                        <span className="sig-date">Enclave Verified</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Properties Panel: Metadata Specifications */}
          <div className="document-info-sidebar document-viewer-sidebar">
            <h4 className="doc-sidebar-section-title">Document Properties</h4>

            <div className="doc-sidebar-specs-list">
              <div className="doc-spec-row">
                <span className="spec-label">File Name</span>
                <span className="spec-value text-mono text-truncate" title={fileName}>
                  {fileName}
                </span>
              </div>
              <div className="doc-spec-row">
                <span className="spec-label">File Size</span>
                <span className="spec-value">{formatBytes(document.fileSize || f.fileSize || 0)}</span>
              </div>
              <div className="doc-spec-row">
                <span className="spec-label">Format</span>
                <span className="spec-value uppercase">{fileFormatInfo.format || 'FILE'}</span>
              </div>
              <div className="doc-spec-row">
                <span className="spec-label">Type</span>
                <span className="spec-value">{docTypeObj.label || 'Document'}</span>
              </div>
              <div className="doc-spec-row">
                <span className="spec-label">Category</span>
                <span className="spec-value">{document.category || 'General'}</span>
              </div>
              <div className="doc-spec-row">
                <span className="spec-label">Owner</span>
                <span className="spec-value">{document.owner || 'You'}</span>
              </div>
              <div className="doc-spec-row">
                <span className="spec-label">Issue Date</span>
                <span className="spec-value">{f.issueDate || document.issueDate || '—'}</span>
              </div>
              <div className="doc-spec-row">
                <span className="spec-label">Expiry Date</span>
                <span
                  className="spec-value"
                  style={{
                    color: expiryInfo.status !== 'none' ? expiryInfo.color : 'inherit',
                    fontWeight: expiryInfo.status !== 'none' ? 600 : 'normal',
                  }}
                >
                  {expiryDate || 'No Expiry'}
                </span>
              </div>
              <div className="doc-spec-row">
                <span className="spec-label">Encryption</span>
                <span className="spec-value text-mono text-xs">AES-256-GCM</span>
              </div>
              <div className="doc-spec-row">
                <span className="spec-label">Last Modified</span>
                <span className="spec-value">
                  {formatDateSafe(document.updated_at || document.created_at || Date.now())}
                </span>
              </div>
              <div className="doc-spec-row">
                <span className="spec-label">Version</span>
                <span className="spec-value">v{document.version || 1}</span>
              </div>
            </div>

            {/* Custom Dynamic Metadata Fields */}
            {customFieldsList.length > 0 && (
              <div className="doc-sidebar-custom-fields-section">
                <span className="spec-label">Custom Metadata</span>
                <div className="doc-sidebar-specs-list">
                  {customFieldsList.map((field, idx) => (
                    <div key={field.id || field.label || idx} className="doc-spec-row">
                      <span className="spec-label">{field.label || field.name || 'Field'}</span>
                      <span className="spec-value">
                        {field.isMasked ? '••••••••' : (field.value || '—')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            <div className="doc-sidebar-tags-section">
              <span className="spec-label">Document Tags</span>
              <div className="doc-sidebar-tags-list">
                {tagsList.length > 0 ? (
                  tagsList.map((t, idx) => (
                    <span key={typeof t === 'string' ? t : idx} className="tag-chip">
                      #{typeof t === 'string' ? t : (t?.name || t?.label || String(t))}
                    </span>
                  ))
                ) : (
                  <span className="text-secondary text-xs">No tags assigned</span>
                )}
              </div>
            </div>

            {/* Description Notes */}
            {f.description && (
              <div className="doc-sidebar-notes-box">
                <span className="spec-label">Notes & Summary</span>
                <p>{f.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Drawer Footer */}
        <div className="card-drawer-footer modal-footer doc-viewer-footer">
          <div className="doc-viewer-footer-left">
            {onDelete && (
              <button
                type="button"
                className="btn btn-danger-outline btn-sm"
                onClick={() => onDelete(document)}
              >
                <Trash2 size={14} />
                <span>Move to Trash</span>
              </button>
            )}
          </div>
          <div className="doc-viewer-footer-right" style={{ display: 'flex', gap: '8px' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Close
            </button>
            {hasEditPermission && onEdit && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  onClose();
                  onEdit(document);
                }}
              >
                <Edit3 size={14} />
                <span>Edit</span>
              </button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
